import React, { useState, useEffect, useRef } from "react";
import { Form, Button, Spinner, Alert } from "react-bootstrap";
import {
  FaBuilding,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaCheckCircle,
  FaRocket,
  FaShieldAlt,
  FaArrowRight,
  FaArrowLeft,
  FaGlobe,
  FaTimesCircle,
} from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import apiClient from "../../utils/apiClient";
import { login as storeAuth } from "../../utils/auth";
import useForceLightTheme from "../../hooks/useForceLightTheme";
import { useBranding } from "../../contexts/BrandingContext";
import vcLogo from "../../Assets/Images/Logos/logo.png";
import vcFavIcon from "../../Assets/Images/Logos/simple_logo.png";
import lmsLogo from "../../Assets/Images/Logos/lms_logo.png";
import lmsFavIcon from "../../Assets/Images/Logos/favicon.png";
import "../Auth/Login.scss";

const isLmsCampus = () => {
  const h = window.location.hostname;
  return h.endsWith("lmscampus.com") || h === "localhost";
};
const logo = isLmsCampus() ? lmsLogo : vcLogo;
const favIcon = isLmsCampus() ? lmsFavIcon : vcFavIcon;

const STEPS = {
  PHONE: 1,
  OTP: 2,
  DETAILS: 3,
};

const RegisterInstitute = () => {
  useForceLightTheme();
  const branding = useBranding();
  const { refreshBranding } = branding;
  const brandLogo = branding.logoUrl || logo;
  const brandFavIcon = branding.faviconUrl || branding.logoUrl || favIcon;
  const brandName = branding.brandName;
  const supportEmail = branding.supportEmail;
  const navigate = useNavigate();

  const [step, setStep] = useState(STEPS.PHONE);
  const [form, setForm] = useState({
    instituteName: "",
    name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    subdomain: "",
  });
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const otpRefs = useRef([]);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [subdomainStatus, setSubdomainStatus] = useState({ checking: false, available: null, message: "" });
  const subdomainTimerRef = useRef(null);

  // Countdown timer for resend
  useEffect(() => {
    if (resendTimer <= 0) return;
    const t = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
    return () => clearTimeout(t);
  }, [resendTimer]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "subdomain") {
      const cleaned = value.toLowerCase().replace(/[^a-z0-9-]/g, "").replace(/^-/, "");
      setForm((prev) => ({ ...prev, subdomain: cleaned }));
      setSubdomainStatus({ checking: false, available: null, message: "" });
      // Debounced availability check
      if (subdomainTimerRef.current) clearTimeout(subdomainTimerRef.current);
      if (cleaned.length >= 3) {
        setSubdomainStatus({ checking: true, available: null, message: "Checking..." });
        subdomainTimerRef.current = setTimeout(() => checkSubdomainAvailability(cleaned), 500);
      }
    } else if (name === "phone") {
      const digitsOnly = value.replace(/[^0-9]/g, "").slice(0, 10);
      setForm((prev) => ({ ...prev, phone: digitsOnly }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
    setMsg({ type: "", text: "" });
  };

  const checkSubdomainAvailability = async (subdomain) => {
    try {
      const { data } = await apiClient.get(`/auth/check-subdomain?subdomain=${encodeURIComponent(subdomain)}`);
      setSubdomainStatus({ checking: false, available: data.available, message: data.message });
    } catch {
      setSubdomainStatus({ checking: false, available: null, message: "Could not check availability." });
    }
  };

  // ─── OTP input handlers ─────────────────────────────────────────────
  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return; // only digits
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1); // single digit
    setOtp(newOtp);
    setMsg({ type: "", text: "" });
    // Auto-focus next input
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    const newOtp = [...otp];
    for (let i = 0; i < 6; i++) {
      newOtp[i] = pasted[i] || "";
    }
    setOtp(newOtp);
    // Focus the last filled input
    const lastIdx = Math.min(pasted.length, 6) - 1;
    otpRefs.current[lastIdx]?.focus();
  };

  // ─── Step 1: Send OTP ───────────────────────────────────────────────
  const handleSendOtp = async (e) => {
    e?.preventDefault();
    setMsg({ type: "", text: "" });
    const cleanPhone = form.phone.replace(/[^0-9]/g, "");
    if (cleanPhone.length < 10) {
      setMsg({ type: "danger", text: "Please enter a valid 10-digit phone number." });
      return;
    }
    setLoading(true);
    try {
      const { data } = await apiClient.post("/auth/otp/send", {
        phone: form.phone,
        purpose: "register-institute",
      });
      setMsg({ type: "success", text: data.message || "OTP sent!" });
      setStep(STEPS.OTP);
      setOtp(["", "", "", "", "", ""]);
      setResendTimer(60);
    } catch (err) {
      setMsg({
        type: "danger",
        text: err?.response?.data?.message || "Failed to send OTP.",
      });
    }
    setLoading(false);
  };

  // ─── Step 2: Verify OTP ─────────────────────────────────────────────
  const handleVerifyOtp = async (e) => {
    e?.preventDefault();
    const otpValue = otp.join("");
    if (otpValue.length !== 6) {
      setMsg({ type: "danger", text: "Please enter the complete 6-digit OTP." });
      return;
    }
    setLoading(true);
    try {
      const { data } = await apiClient.post("/auth/otp/verify", {
        phone: form.phone,
        otp: otpValue,
        purpose: "register-institute",
      });
      if (data.verified) {
        setPhoneVerified(true);
        setMsg({ type: "success", text: "Phone verified! Complete your details." });
        setStep(STEPS.DETAILS);
      }
    } catch (err) {
      setMsg({
        type: "danger",
        text: err?.response?.data?.message || "OTP verification failed.",
      });
    }
    setLoading(false);
  };

  // ─── Step 3: Submit registration ────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg({ type: "", text: "" });

    if (!acceptedTerms) {
      setMsg({ type: "danger", text: "Please accept the Terms & Conditions to continue." });
      return;
    }
    if (form.password !== form.confirmPassword) {
      setMsg({ type: "danger", text: "Passwords do not match." });
      return;
    }
    if (form.password.length < 6) {
      setMsg({ type: "danger", text: "Password must be at least 6 characters." });
      return;
    }
    if (!phoneVerified) {
      setMsg({ type: "danger", text: "Please verify your phone number first." });
      return;
    }

    setLoading(true);
    try {
      const { data } = await apiClient.post("/auth/register-institute", {
        instituteName: form.instituteName,
        name: form.name,
        email: form.email,
        phone: form.phone,
        password: form.password,
        subdomain: form.subdomain || undefined,
      });

      if (data.token) {
        storeAuth(data);
        if (refreshBranding) refreshBranding();

        // If backend returned a portalUrl (subdomain), redirect there
        // so the user lands on their own institute subdomain
        const portalUrl = data.institute?.portalUrl;
        const isLocalhost = window.location.hostname === 'localhost';

        setMsg({
          type: "success",
          text: `\uD83C\uDF89 ${data.message || "Institute created!"} ${portalUrl && !isLocalhost ? `Your portal: ${portalUrl}` : ''} Redirecting...`,
        });

        if (portalUrl && !isLocalhost) {
          // Redirect to the institute's own subdomain dashboard
          // Pass token via URL params for cross-subdomain handoff
          const tokenParam = encodeURIComponent(data.token);
          const userParam = encodeURIComponent(JSON.stringify(data.user));
          setTimeout(() => {
            window.location.href = `${portalUrl}/dashboard?_token=${tokenParam}&_user=${userParam}`;
          }, 1500);
        } else {
          // Localhost or no subdomain — stay on same origin
          setTimeout(() => navigate("/dashboard"), 1200);
        }
      }
    } catch (err) {
      setMsg({
        type: "danger",
        text: err?.response?.data?.message || "Registration failed. Please try again.",
      });
    }
    setLoading(false);
  };

  // ─── Step progress indicator ────────────────────────────────────────
  const StepIndicator = () => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 24 }}>
      {[STEPS.PHONE, STEPS.OTP, STEPS.DETAILS].map((s, idx) => (
        <React.Fragment key={s}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "0.85rem",
              fontWeight: 600,
              background: step >= s ? "#4069e5" : "#e2e8f0",
              color: step >= s ? "#fff" : "#94a3b8",
              transition: "all 0.3s",
            }}
          >
            {step > s ? <FaCheckCircle size={14} /> : idx + 1}
          </div>
          {idx < 2 && (
            <div
              style={{
                width: 40,
                height: 2,
                background: step > s ? "#4069e5" : "#e2e8f0",
                transition: "background 0.3s",
              }}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <div className="auth-page">
      {/* Mobile top navbar */}
      <div className="auth-mobile-navbar">
        <Link to="/" className="auth-mobile-navbar-logo">
          <img src={brandFavIcon} alt="Logo" />
          <span>{brandName}</span>
        </Link>
        <Link to="/login" className="auth-mobile-navbar-btn">
          Sign In
        </Link>
      </div>

      {/* Mobile hero section */}
      <div className="auth-mobile-hero">
        <div className="auth-mobile-hero-bg" />
        <div className="auth-mobile-hero-content">
          <div className="auth-mobile-hero-badge">
            <img src={brandFavIcon} alt="Logo" />
          </div>
          <h1 className="auth-mobile-hero-title">Start Your Institute</h1>
          <p className="auth-mobile-hero-sub">
            Set up your coaching institute in 2 minutes
          </p>
        </div>
      </div>

      <div className="auth-container">
        {/* Left: Branding Panel */}
        <div className="auth-brand-panel">
          <div className="auth-brand-content">
            <div className="auth-brand-badge">
              <img src={brandFavIcon} alt="Logo" className="auth-brand-icon" />
            </div>
            <h1 className="auth-brand-title">{brandName}</h1>
            <p className="auth-brand-subtitle">
              The complete LMS platform for coaching institutes
            </p>

            <div className="auth-brand-features" style={{ marginTop: "2rem" }}>
              <div className="auth-feature-item">
                <FaCheckCircle style={{ color: "#22c55e", marginRight: 8, flexShrink: 0 }} />
                <span>14-day free trial — no credit card needed</span>
              </div>
              <div className="auth-feature-item">
                <FaCheckCircle style={{ color: "#22c55e", marginRight: 8, flexShrink: 0 }} />
                <span>Manage courses, batches & students</span>
              </div>
              <div className="auth-feature-item">
                <FaCheckCircle style={{ color: "#22c55e", marginRight: 8, flexShrink: 0 }} />
                <span>Live Zoom classes with recording</span>
              </div>
              <div className="auth-feature-item">
                <FaCheckCircle style={{ color: "#22c55e", marginRight: 8, flexShrink: 0 }} />
                <span>AI-powered mock interviews</span>
              </div>
              <div className="auth-feature-item">
                <FaCheckCircle style={{ color: "#22c55e", marginRight: 8, flexShrink: 0 }} />
                <span>Fee management & payment tracking</span>
              </div>
              <div className="auth-feature-item">
                <FaCheckCircle style={{ color: "#22c55e", marginRight: 8, flexShrink: 0 }} />
                <span>Assignments & coding practice</span>
              </div>
            </div>
          </div>
          <div className="auth-brand-footer">
            <img src={brandLogo} alt={brandName} className="auth-brand-logo" />
          </div>
        </div>

        {/* Right: Form Panel */}
        <div className="auth-form-panel">
          <div className="auth-form-wrapper">
            <div className="auth-form-header">
              <h2>
                <FaRocket style={{ marginRight: 8, color: "#ff9800" }} />
                Start Your Free Trial
              </h2>
              <p>
                {step === STEPS.PHONE && "Verify your phone number to get started."}
                {step === STEPS.OTP && "Enter the 6-digit OTP sent to your phone."}
                {step === STEPS.DETAILS && "Almost there! Fill in your details."}
              </p>
            </div>

            <StepIndicator />

            {/* ═══════ STEP 1: Phone Number ═══════ */}
            {step === STEPS.PHONE && (
              <Form onSubmit={handleSendOtp} className="auth-form">
                <div className="auth-input-group">
                  <label>
                    Phone Number
                  </label>
                  <div className="auth-input-wrapper">
                    <input
                      type="tel"
                      inputMode="numeric"
                      placeholder="Enter your 10-digit phone number"
                      name="phone"
                      autoComplete="tel"
                      value={form.phone}
                      onChange={handleChange}
                      maxLength={10}
                      required
                      autoFocus
                    />
                  </div>
                  <span style={{ fontSize: "0.8rem", color: "#64748b", marginTop: 4, display: "block" }}>
                    We'll send an OTP to verify this number.
                  </span>
                </div>

                {msg.text && (
                  <Alert variant={msg.type} className="auth-alert">
                    {msg.text}
                  </Alert>
                )}

                <Button type="submit" className="auth-submit-btn" disabled={loading}>
                  {loading ? (
                    <Spinner size="sm" animation="border" />
                  ) : (
                    <>
                      Send OTP <FaArrowRight style={{ marginLeft: 6 }} />
                    </>
                  )}
                </Button>
              </Form>
            )}

            {/* ═══════ STEP 2: OTP Verification ═══════ */}
            {step === STEPS.OTP && (
              <Form onSubmit={handleVerifyOtp} className="auth-form">
                <div className="auth-input-group" style={{ textAlign: "center" }}>
                  <label style={{ marginBottom: 8 }}>
                    <FaShieldAlt style={{ marginRight: 6, color: "#4069e5" }} />
                    Enter OTP sent to {form.phone}
                  </label>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      gap: 8,
                      marginTop: 8,
                    }}
                    onPaste={handleOtpPaste}
                  >
                    {otp.map((digit, idx) => (
                      <input
                        key={idx}
                        ref={(el) => (otpRefs.current[idx] = el)}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleOtpChange(idx, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                        autoFocus={idx === 0}
                        style={{
                          width: 46,
                          height: 52,
                          textAlign: "center",
                          fontSize: "1.3rem",
                          fontWeight: 600,
                          border: "2px solid #e2e8f0",
                          borderRadius: 10,
                          outline: "none",
                          transition: "border-color 0.2s",
                          borderColor: digit ? "#4069e5" : "#e2e8f0",
                        }}
                      />
                    ))}
                  </div>
                  <div style={{ marginTop: 12, fontSize: "0.85rem", color: "#64748b" }}>
                    {resendTimer > 0 ? (
                      <span>Resend OTP in {resendTimer}s</span>
                    ) : (
                      <button
                        type="button"
                        onClick={handleSendOtp}
                        disabled={loading}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#4069e5",
                          cursor: "pointer",
                          fontWeight: 600,
                          textDecoration: "underline",
                        }}
                      >
                        Resend OTP
                      </button>
                    )}
                  </div>
                </div>

                {msg.text && (
                  <Alert variant={msg.type} className="auth-alert">
                    {msg.text}
                  </Alert>
                )}

                <div style={{ display: "flex", gap: 12 }}>
                  <Button
                    type="button"
                    variant="outline-secondary"
                    className="auth-submit-btn"
                    style={{ flex: 1, background: "#f8fafc", color: "#475569" }}
                    onClick={() => { setStep(STEPS.PHONE); setMsg({ type: "", text: "" }); }}
                  >
                    <FaArrowLeft style={{ marginRight: 6 }} /> Back
                  </Button>
                  <Button
                    type="submit"
                    className="auth-submit-btn"
                    style={{ flex: 2 }}
                    disabled={loading || otp.join("").length !== 6}
                  >
                    {loading ? (
                      <Spinner size="sm" animation="border" />
                    ) : (
                      <>
                        Verify OTP <FaArrowRight style={{ marginLeft: 6 }} />
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            )}

            {/* ═══════ STEP 3: Institute Details ═══════ */}
            {step === STEPS.DETAILS && (
              <Form onSubmit={handleSubmit} className="auth-form">
                {/* Phone verified badge */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "8px 12px",
                    background: "#f0fdf4",
                    borderRadius: 8,
                    marginBottom: 12,
                    fontSize: "0.85rem",
                    color: "#16a34a",
                    fontWeight: 500,
                  }}
                >
                  <FaCheckCircle /> Phone verified: {form.phone}
                </div>

                {/* Institute Name */}
                <div className="auth-input-group">
                  <label>Institute Name</label>
                  <div className="auth-input-wrapper">
                    <FaBuilding className="auth-input-icon" />
                    <input
                      type="text"
                      placeholder="e.g. ABC Academy, TechGuru Classes"
                      name="instituteName"
                      value={form.instituteName}
                      onChange={handleChange}
                      required
                      autoFocus
                    />
                  </div>
                </div>

                {/* Subdomain */}
                <div className="auth-input-group">
                  <label>Choose Your Portal URL</label>
                  <div className="auth-input-wrapper">
                    <FaGlobe className="auth-input-icon" />
                    <input
                      type="text"
                      placeholder="your-institute"
                      name="subdomain"
                      value={form.subdomain}
                      onChange={handleChange}
                      maxLength={30}
                      style={{ borderColor: form.subdomain.length >= 3 ? (subdomainStatus.available === true ? '#22c55e' : subdomainStatus.available === false ? '#ef4444' : undefined) : undefined }}
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 }}>
                    <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                      {form.subdomain ? (
                        <><strong>{form.subdomain}</strong>.lmscampus.com</>
                      ) : (
                        'your-institute.lmscampus.com'
                      )}
                    </span>
                    {form.subdomain.length >= 3 && (
                      <span style={{ fontSize: '0.8rem', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4, color: subdomainStatus.checking ? '#64748b' : subdomainStatus.available ? '#22c55e' : '#ef4444' }}>
                        {subdomainStatus.checking ? (
                          <Spinner size="sm" animation="border" style={{ width: 12, height: 12 }} />
                        ) : subdomainStatus.available === true ? (
                          <FaCheckCircle size={12} />
                        ) : subdomainStatus.available === false ? (
                          <FaTimesCircle size={12} />
                        ) : null}
                        {subdomainStatus.message}
                      </span>
                    )}
                  </div>
                  <span style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: 2, display: 'block' }}>
                    Optional — leave empty to auto-generate from institute name.
                  </span>
                </div>

                {/* Admin Name */}
                <div className="auth-input-group">
                  <label>Your Name</label>
                  <div className="auth-input-wrapper">
                    <FaUser className="auth-input-icon" />
                    <input
                      type="text"
                      placeholder="Your full name"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                {/* Email */}
                <div className="auth-input-group">
                  <label>Email Address</label>
                  <div className="auth-input-wrapper">
                    <FaEnvelope className="auth-input-icon" />
                    <input
                      type="email"
                      placeholder="admin@yourinstitute.com"
                      name="email"
                      autoComplete="email"
                      value={form.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                {/* Password */}
                <div className="auth-input-group">
                  <label>Password</label>
                  <div className="auth-input-wrapper">
                    <FaLock className="auth-input-icon" />
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="Min. 6 characters"
                      name="password"
                      autoComplete="new-password"
                      value={form.password}
                      onChange={handleChange}
                      required
                      minLength={6}
                    />
                    <button
                      type="button"
                      className="auth-password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex={-1}
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>

                {/* Confirm Password */}
                <div className="auth-input-group">
                  <label>Confirm Password</label>
                  <div className="auth-input-wrapper">
                    <FaLock className="auth-input-icon" />
                    <input
                      type={showConfirm ? "text" : "password"}
                      placeholder="Confirm your password"
                      name="confirmPassword"
                      autoComplete="new-password"
                      value={form.confirmPassword}
                      onChange={handleChange}
                      required
                    />
                    <button
                      type="button"
                      className="auth-password-toggle"
                      onClick={() => setShowConfirm(!showConfirm)}
                      tabIndex={-1}
                    >
                      {showConfirm ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>

                {/* Terms & Conditions */}
                <div className="auth-terms-group" style={{ marginTop: "8px" }}>
                  <Form.Check
                    type="checkbox"
                    id="register-terms"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    label={
                      <span style={{ fontSize: "0.85rem", color: "#64748b" }}>
                        I agree to the{" "}
                        <Link to="/terms-and-conditions" target="_blank" style={{ color: "#4069e5" }}>
                          Terms & Conditions
                        </Link>
                      </span>
                    }
                  />
                </div>

                {msg.text && (
                  <Alert variant={msg.type} className="auth-alert">
                    {msg.text}
                  </Alert>
                )}

                <Button type="submit" className="auth-submit-btn" disabled={loading}>
                  {loading ? (
                    <Spinner size="sm" animation="border" />
                  ) : (
                    <>
                      <FaRocket style={{ marginRight: 6 }} /> Start Free Trial
                    </>
                  )}
                </Button>
              </Form>
            )}

            <div className="auth-footer-text">
              Already have an account?{" "}
              <Link to="/login" className="auth-action-link">
                Sign in
              </Link>
            </div>

            {supportEmail && (
              <div className="auth-footer-text" style={{ fontSize: "0.8rem", marginTop: "0.5rem" }}>
                Need help?{" "}
                <a href={`mailto:${supportEmail}`} className="auth-action-link">
                  {supportEmail}
                </a>
              </div>
            )}

            <div className="auth-footer-logo">
              <img src={brandLogo} alt={brandName} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterInstitute;
